import React from 'react';
import Icon from '../../../components/AppIcon';
import SpeedoGauge from './SpeedoGauge';

const MetricCard = ({
  title,
  value,
  unit = '',
  trend = 0,
  status = 'primary',
  icon = 'TrendingUp',
  description = '',
  loadRiskCategory = null,
  qualitativeSummary = null,
  dailyChange = null,
  sevenDayTrend = null,
  wellnessBreakdown = null,
  hasWellnessData = false,
  isLoadingWellness = false,
  readinessData = null,
  isLoadingReadiness = false,
  wellnessQualitativeRating = null,
  wellnessQualitativeGuidance = null,
  loadRatioQualitativeRating = null,
  todayAULoad = null,
  yesterdayAULoad = null,
  dailyChangePercent = null
}) => {
  const getStatusColor = (loadRiskCategory, status) => {
    // Use loadRiskCategory for Load Ratio cards, fallback to status for other cards
    if (loadRiskCategory) {
      switch (loadRiskCategory) {
        case 'low_load_risk':
          return 'text-blue-600 bg-blue-600/10 border-blue-600/20';
        case 'low':
          return 'text-green-600 bg-green-600/10 border-green-600/20';
        case 'elevated':
          return 'text-orange-600 bg-orange-600/10 border-orange-600/20';
        case 'high':
          return 'text-red-600 bg-red-600/10 border-red-600/20';
        default:
          return 'text-text-secondary bg-muted border-border';
      }
    }
    
    // Fallback for other metric cards
    switch (status) {
      case 'low':
        return 'text-success bg-success/10 border-success/20';
      case 'green':
        return 'text-success bg-success/10 border-success/20';
      case 'amber':
        return 'text-warning bg-warning/10 border-warning/20';
      case 'red':
        return 'text-error bg-error/10 border-error/20';
      default:
        return 'text-text-secondary bg-muted border-border';
    }
  };

  const getCardBackgroundColor = (loadRiskCategory) => {
    if (!loadRiskCategory) return 'bg-surface';
    
    switch (loadRiskCategory) {
      case 'low_load_risk':
        return 'bg-card border-border';
      case 'low':
        return 'bg-card border-border';
      case 'elevated':
        return 'bg-card border-border';
      case 'high':
        return 'bg-card border-border';
      default:
        return 'bg-surface border-border';
    }
  };

  const getTrendIcon = (trend) => {
    if (trend > 0) return 'TrendingUp';
    if (trend < 0) return 'TrendingDown';
    return 'Minus';
  };

  const getTrendColor = (trend) => {
    // For Wellness Score, invert the logic: upward trend is bad (red), downward trend is good (green)
    if (title === 'Wellness Score') {
      if (trend > 0) return 'text-error';
      if (trend < 0) return 'text-success';
      return 'text-text-secondary';
    }
    
    // For other metrics, keep normal logic: upward trend is good (green), downward trend is bad (red)
    if (trend > 0) return 'text-success';
    if (trend < 0) return 'text-error';
    return 'text-text-secondary';
  };

  // Helper function to get trend indicator text
  const getTrendIndicatorText = (sevenDayTrend) => {
    if (!sevenDayTrend) return '';
    
    switch (sevenDayTrend) {
      case 'increasing':
        return '↗️ Trending Up';
      case 'decreasing':
        return '↘️ Trending Down';
      case 'stable':
        return '→ Stable';
      default:
        return '';
    }
  };

  // NEW: Get wellness score color based on qualitative rating
  const getWellnessScoreColor = (rating) => {
    if (!rating) return 'text-text-primary';
    switch (rating) {
      case 'Excellent':
        return 'text-success';
      case 'Good':
        return 'text-accent';
      case 'Fair':
        return 'text-warning';
      case 'Poor':
        return 'text-error';
      default:
        return 'text-text-primary';
    }
  };

  // NEW: Get Load Ratio color based on qualitative rating
  const getLoadRatioColor = (rating) => {
    if (!rating) return 'text-text-primary';
    switch (rating) {
      case 'Low Load':
        return 'text-blue-600';
      case 'Optimal Zone':
        return 'text-green-600';
      case 'Elevated Risk':
        return 'text-orange-600';
      case 'High Risk':
        return 'text-red-600';
      default:
        return 'text-text-primary';
    }
  };

  // FIXED: Determine if we should show the risk badge - exclude 'error', 'warning', 'success' statuses
  // Only show risk badge for Load Ratio cards that have a loadRiskCategory (not status-based styling)
  const shouldShowRiskBadge = (status, loadRiskCategory) => {
    // Never show risk badge if this is a status-based metric (wellness, etc.)
    if (!loadRiskCategory) return false;
    
    // For Load Ratio cards, always show the risk band label from loadRiskCategory, not status
    return true;
  };

  // FIXED: Get proper risk badge label from loadRiskCategory instead of status
  const getRiskBadgeLabel = (loadRiskCategory) => {
    if (!loadRiskCategory) return '';
    
    switch (loadRiskCategory) {
      case 'low_load_risk':
        return 'Low-load Risk';
      case 'low':
        return 'Optimal Zone';
      case 'elevated':
        return 'Elevated Risk';
      case 'high':
        return 'High Risk';
      case 'insufficient_data':
        return 'Insufficient Data';
      default:
        return '';
    }
  };

  // NEW: Readiness Index specific rendering
  if (title === 'Readiness Index' && readinessData) {
    // Add this helper function for status colors
    const statusColors = {
      primary: { bg: 'bg-primary/10' },
      success: { bg: 'bg-success/10' },
      warning: { bg: 'bg-warning/10' },
      error: { bg: 'bg-error/10' },
      default: { bg: 'bg-muted' }
    };

    // Add this helper function to get icon
    const getIcon = (iconName) => {
      return <Icon name={iconName} size={20} color="currentColor" strokeWidth={2} />;
    };

    // Define thresholds matching the existing logic
    const readinessThresholds = {
      green: 0.33,
      amber: 0.66
    };

    return (
      <div className="bg-card rounded-xl shadow-lg p-6 border-l-4 border-primary hover:shadow-xl transition-shadow duration-300">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-3">
            <div className={statusColors?.[status]?.bg || 'bg-muted'}>
              {getIcon(icon)}
            </div>
            <div>
              <h3 className="text-sm font-semibold text-text-secondary uppercase tracking-wide">
                {title}
              </h3>
              <p className="text-xs text-text-secondary mt-0.5">{description}</p>
            </div>
          </div>
          
          {/* Readiness Band Badge */}
          {readinessData?.band && readinessData?.band !== 'insufficient_data' && (
            <div className={`px-3 py-1 rounded-full text-xs font-semibold ${
              readinessData?.band === 'green' ? 'bg-success/10 text-success' :
              readinessData?.band === 'amber'? 'bg-warning/10 text-warning' : 'bg-error/10 text-error'
            }`}>
              {readinessData?.band?.toUpperCase()}
            </div>
          )}
        </div>
        {/* Main Value Display */}
        <div className="mb-4">
          <div className="flex items-baseline space-x-2">
            <span className={`text-4xl font-bold ${
              readinessData?.hasData ? 'text-text-primary' : 'text-text-secondary'
            }`}>
              {isLoadingReadiness ? (
                <div className="w-16 h-10 bg-muted animate-pulse rounded"></div>
              ) : (
                value
              )}
            </span>
            {unit && <span className="text-lg text-text-secondary">{unit}</span>}
          </div>
        </div>
        
        {/* Speedometer Gauge */}
        {readinessData?.hasData && !isLoadingReadiness && (
          <div className="mb-6">
            <SpeedoGauge 
              value={parseFloat(value) || 0} 
              status={readinessData?.band?.toUpperCase() || ''} 
              thresholds={readinessThresholds}
            />
          </div>
        )}
        
        {/* Recommendation Section */}
        {readinessData?.hasData && readinessData?.recommendation && (
          <div className="mb-4 p-4 bg-muted rounded-lg border border-border">
            <div className="flex items-start space-x-2">
              <svg 
                className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" 
                fill="none" 
                stroke="currentColor" 
                viewBox="0 0 24 24"
              >
                <path 
                  strokeLinecap="round" 
                  strokeLinejoin="round" 
                  strokeWidth={2} 
                  d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" 
                />
              </svg>
              <div className="flex-1">
                <p className="text-xs font-semibold text-text-secondary mb-1">Recommendation:</p>
                <p className="text-sm text-text-secondary leading-relaxed">
                  {readinessData?.recommendation}
                </p>
              </div>
            </div>
          </div>
        )}
        {/* Component Breakdown */}
        {readinessData?.hasData && readinessData?.components && (
          <div className="space-y-3 mb-4">
            <div className="text-xs font-semibold text-text-secondary uppercase tracking-wide">
              Risk Components
            </div>
            
            {/* Hooper (Wellness) Risk */}
            <div className="space-y-1">
              <div className="flex justify-between items-center">
                <span className="text-xs text-text-secondary">Wellness Risk</span>
                <span className="text-xs font-semibold text-text-primary">
                  {(readinessData?.components?.hooperRisk * 100)?.toFixed(0)}%
                </span>
              </div>
              <div className="w-full bg-muted rounded-full h-2">
                <div 
                  className={`h-2 rounded-full transition-all duration-500 ${
                    readinessData?.components?.hooperRisk < 0.33 ? 'bg-green-500' :
                    readinessData?.components?.hooperRisk < 0.66 ? 'bg-amber-500': 'bg-red-500'
                  }`}
                  style={{ width: `${readinessData?.components?.hooperRisk * 100}%` }}
                ></div>
              </div>
              <p className="text-xs text-text-secondary">
                State: <span className="font-semibold">{readinessData?.states?.wellnessState?.replace('_', ' ')}</span>
                {readinessData?.todayValues?.hooperIndex && 
                  ` (Hooper: ${readinessData?.todayValues?.hooperIndex}/28)`}
              </p>
            </div>

            {/* ACWR Risk */}
            <div className="space-y-1">
              <div className="flex justify-between items-center">
                <span className="text-xs text-text-secondary">Load Ratio Risk</span>
                <span className="text-xs font-semibold text-text-primary">
                  {(readinessData?.components?.acwrRisk * 100)?.toFixed(0)}%
                </span>
              </div>
              <div className="w-full bg-muted rounded-full h-2">
                <div 
                  className={`h-2 rounded-full transition-all duration-500 ${
                    readinessData?.components?.acwrRisk < 0.33 ? 'bg-green-500' :
                    readinessData?.components?.acwrRisk < 0.66 ? 'bg-amber-500': 'bg-red-500'
                  }`}
                  style={{ width: `${readinessData?.components?.acwrRisk * 100}%` }}
                ></div>
              </div>
              <p className="text-xs text-text-secondary">
                State: <span className="font-semibold">{readinessData?.states?.acwrState?.replace('_', ' ')}</span>
                {readinessData?.todayValues?.acwr && 
                  ` (ACWR: ${readinessData?.todayValues?.acwr})`}
              </p>
            </div>
          </div>
        )}
        {/* Insufficient Data Message */}
        {!readinessData?.hasData && (
          <div className="p-4 bg-muted rounded-lg border border-border">
            <p className="text-sm text-text-primary">
              {readinessData?.recommendation || 'Loading readiness data...'}
            </p>
          </div>
        )}
        {/* Technical Details (Collapsible) */}
        {readinessData?.hasData && readinessData?.components?.zScores && (
          <details className="mt-4">
            <summary className="text-xs text-text-secondary cursor-pointer hover:text-text-primary font-medium">
              View Technical Details
            </summary>
            <div className="mt-2 p-3 bg-muted rounded text-xs space-y-1">
              <div className="flex justify-between">
                <span className="text-text-secondary">Wellness Z-Score:</span>
                <span className="font-mono text-text-primary">{readinessData?.components?.zScores?.hooper}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-text-secondary">ACWR Z-Score:</span>
                <span className="font-mono text-text-primary">{readinessData?.components?.zScores?.acwr}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-text-secondary">Composite Index:</span>
                <span className="font-mono text-text-primary">{readinessData?.value?.toFixed(2)}</span>
              </div>
            </div>
          </details>
        )}
      </div>
    );
  }

  return (
    <div className={`${getCardBackgroundColor(loadRiskCategory)} border rounded-lg p-6 data-card smooth-transition hover:shadow-lg`}>
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center space-x-3">
          <div className={`p-2 rounded-lg ${getStatusColor(loadRiskCategory, status)}`}>
            <Icon name={icon} size={20} color="currentColor" strokeWidth={2} />
          </div>
          <div>
            <h3 className="text-sm font-medium text-text-secondary">{title}</h3>
            <p className="text-xs text-text-secondary opacity-75">{description}</p>
          </div>
        </div>
        {trend !== undefined && (
          <div className={`flex items-center space-x-1 ${getTrendColor(trend)}`}>
            <Icon name={getTrendIcon(trend)} size={16} color="currentColor" strokeWidth={2} />
            <span className="text-xs font-medium">
              {Math.abs(trend)?.toFixed(1)}%
            </span>
          </div>
        )}
      </div>
      <div className="flex items-baseline space-x-2">
        <span className={`text-2xl font-semibold ${
          title === 'Wellness Score' && wellnessQualitativeRating 
            ? getWellnessScoreColor(wellnessQualitativeRating)
            : title === 'Load Ratio (ACWR)' && loadRatioQualitativeRating
            ? getLoadRatioColor(loadRatioQualitativeRating)
            : 'text-text-primary'
        }`}>{value}</span>
        {unit && <span className="text-sm text-text-secondary">{unit}</span>}
        {title === 'Wellness Score' && wellnessQualitativeRating && (
          <span className={`text-sm font-semibold ${
            getWellnessScoreColor(wellnessQualitativeRating)
          }`}>({wellnessQualitativeRating})</span>
        )}
        {title === 'Load Ratio (ACWR)' && loadRatioQualitativeRating && (
          <span className={`text-sm font-semibold ${
            getLoadRatioColor(loadRatioQualitativeRating)
          }`}>({loadRatioQualitativeRating})</span>
        )}
      </div>
      {/* Wellness Score Breakdown - NEW */}
      {wellnessBreakdown && hasWellnessData && (
        <div className="mt-4 pt-4 border-t border-border">
          <div className="text-xs text-text-secondary space-y-2">
            <p className="font-semibold mb-2 text-text-primary">Today's Wellness Breakdown:</p>
            <div className="grid grid-cols-2 gap-2">
              <div className="flex justify-between">
                <span>Sleep Quality:</span>
                <span className="font-medium">{wellnessBreakdown?.sleep}/7</span>
              </div>
              <div className="flex justify-between">
                <span>Fatigue:</span>
                <span className="font-medium">{wellnessBreakdown?.fatigue}/7</span>
              </div>
              <div className="flex justify-between">
                <span>Muscle Soreness:</span>
                <span className="font-medium">{wellnessBreakdown?.soreness}/7</span>
              </div>
              <div className="flex justify-between">
                <span>Stress:</span>
                <span className="font-medium">{wellnessBreakdown?.stress}/7</span>
              </div>
            </div>
          </div>
        </div>
      )}
      {/* Wellness Trend and Qualitative Rating - NEW */}
      {hasWellnessData && qualitativeSummary && dailyChange !== null && dailyChange !== undefined && (
        <div className="mt-3 p-3 bg-muted rounded-lg border border-border">
          <p className="text-xs text-text-secondary leading-relaxed">
            Your wellness score {dailyChange > 0 ? 'increased' : dailyChange < 0 ? 'decreased' : 'remained stable'} 
            {dailyChange !== 0 && ` by ${Math.abs(dailyChange)?.toFixed(1)}%`} compared to yesterday. 
            This score is considered <span className="font-semibold">{qualitativeSummary}</span>.
          </p>
        </div>
      )}
      {/* No Wellness Data Prompt - NEW */}
      {!hasWellnessData && !isLoadingWellness && title === 'Wellness Score' && (
        <div className="mt-4 pt-4 border-t border-border">
          <div className="bg-muted border border-border rounded-lg p-3">
            <div className="flex items-start space-x-2">
              <div className="flex-shrink-0">
                <svg 
                  className="w-5 h-5 text-warning" 
                  fill="currentColor" 
                  viewBox="0 0 20 20"
                >
                  <path 
                    fillRule="evenodd" 
                    d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" 
                    clipRule="evenodd" 
                  />
                </svg>
              </div>
              <div className="flex-1">
                <p className="text-xs text-text-primary font-medium">
                  No wellness data for today
                </p>
                <p className="text-xs text-text-secondary mt-1">
                  Please complete your daily wellness entry to see your Total Wellness Score and track your recovery.
                </p>
              </div>
            </div>
          </div>
        </div>
      )}
      {/* Loading State - NEW */}
      {isLoadingWellness && title === 'Wellness Score' && (
        <div className="mt-4 pt-4 border-t border-border">
          <div className="flex items-center justify-center space-x-2 text-text-secondary">
            <div className="w-4 h-4 border-2 border-border border-t-transparent rounded-full animate-spin"></div>
            <span className="text-xs">Loading wellness data...</span>
          </div>
        </div>
      )}
      {/* Daily Change Badge */}
      {title === 'Wellness Score' && dailyChange !== null && dailyChange !== undefined && (
        <div className="mt-2">
          <div className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
            dailyChange > 0 ? 'bg-blue-600/10 text-blue-600' : 
            dailyChange < 0 ? 'bg-success/10 text-success': 'bg-muted text-text-secondary'
          }`}>
            {dailyChange > 0 ? '▲' : dailyChange < 0 ? '▼' : '='} 
            {' '}{Math.abs(dailyChange)?.toFixed(2)} from yesterday
          </div>
        </div>
      )}
      {/* Load Ratio Daily Change Badge */}
      {title === 'Load Ratio (ACWR)' && dailyChange !== null && dailyChange !== undefined && dailyChangePercent !== null && (
        <div className="mt-2">
          <div className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
            dailyChange > 0 ? 'bg-warning/10 text-warning' : 
            dailyChange < 0 ? 'bg-success/10 text-success': 'bg-muted text-text-secondary'
          }`}>
            {dailyChange > 0 ? '▲' : dailyChange < 0 ? '▼' : '='} 
            {' '}{dailyChange > 0 ? 'Increased' : dailyChange < 0 ? 'Decreased' : 'Stable'} by {Math.abs(dailyChangePercent)?.toFixed(1)}% from yesterday
          </div>
        </div>
      )}
      {/* FIXED: Risk Badge - now uses loadRiskCategory label instead of status */}
      {shouldShowRiskBadge(status, loadRiskCategory) && (
        <div className="mt-3">
          <div className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(loadRiskCategory, status)}`}>
            {getRiskBadgeLabel(loadRiskCategory)}
          </div>
        </div>
      )}
      {/* 7-Day Trend Indicator */}
      {sevenDayTrend && sevenDayTrend !== 'insufficient_data' && (
        <div className="mt-3">
          <div className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-muted text-text-primary border border-border">
            {getTrendIndicatorText(sevenDayTrend)}
          </div>
        </div>
      )}
      {/* Qualitative Summary */}
      {title === 'Wellness Score' && qualitativeSummary && (
        <div className="mt-4 pt-4 border-t border-border">
          <div className="text-xs text-text-secondary leading-relaxed">
            <p className="font-semibold mb-1 text-text-primary">Wellness Feedback:</p>
            <p>
              {wellnessQualitativeGuidance}
            </p>
          </div>
        </div>
      )}
      {/* Load Ratio Qualitative Summary */}
      {title === 'Load Ratio (ACWR)' && dailyChange !== null && dailyChangePercent !== null && todayAULoad !== null && yesterdayAULoad !== null && (
        <div className="mt-4 pt-4 border-t border-border">
          <div className="text-xs text-text-secondary leading-relaxed space-y-2">
            <p className="font-semibold text-text-primary">Load Ratio Feedback:</p>
            <p>
              Your load ratio has {dailyChange > 0 ? 'increased' : dailyChange < 0 ? 'decreased' : 'remained stable'}
              {dailyChange !== 0 && ` by ${Math.abs(dailyChangePercent)?.toFixed(1)}%`} compared to yesterday.
              {' '}This ratio is in the <span className={`font-semibold ${
                getLoadRatioColor(loadRatioQualitativeRating)
              }`}>{loadRatioQualitativeRating}</span>.
            </p>
            <div className="pt-2 space-y-1">
              <div className="flex justify-between items-center">
                <span className="text-text-secondary">Today's AU Load:</span>
                <span className="font-semibold text-text-primary">{todayAULoad} AU</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-text-secondary">Yesterday's AU Load:</span>
                <span className="font-semibold text-text-primary">{yesterdayAULoad} AU</span>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MetricCard;